'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import type { DealerFormType } from '@/app/api/lms/type';
import { LocationSelector } from '@/app/components/form-kit/fields/location-selector';
import { CtaButtonBottom } from '@/app/components/sections/special-offer/detail/search-dealer-contact/cta-button-bottom';
import { DealerInfoCard } from '@/app/components/sections/special-offer/detail/search-dealer-contact/dealer-info-card';
import type { SearchDealerContactProps } from '@/app/components/sections/special-offer/detail/search-dealer-contact/types';
import { SPECIAL_OFFER_DATA_TEST, SPECIAL_OFFER_EVENT_NAME } from '@/app/components/sections/special-offer/tracking';
import { useInView } from '@/app/hooks/useInView';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
import { useForm } from '@/app/lib/forms/hooks/useForm';
import { createBaseLocationSchema } from '@/app/lib/forms/schemas/lead-form.schema';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { scrollIntoViewIfNeeded } from '@/app/utils/scrollIntoViewIfNeed';
import { trackDynamicEvents } from '@/tag-manager';
const searchDealerSchema = createBaseLocationSchema();
export const SearchDealerContact = t0 => {
  const $ = _c(43);
  const {
    provinces: t1,
    title: t2,
    hideButton: t3,
    className
  } = t0;
  let t4;
  if ($[0] !== t1) {
    t4 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const provinces = t4;
  const title = t2 === undefined ? "\u0E19\u0E31\u0E14\u0E2B\u0E21\u0E32\u0E22\u0E23\u0E31\u0E1A\u0E2A\u0E34\u0E17\u0E18\u0E34\u0E4C" : t2;
  const hideButton = t3 === undefined ? false : t3;
  const isDesktop = useMediaQuery("(min-width: 1280px)");
  const contentRef = useRef(null);
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      once: false
    };
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const isFormVisible = useInView(contentRef, t5);
  const [selectedDealer, setSelectedDealer] = useState(null);
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = dealer => {
      setSelectedDealer(dealer);
    };
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const handleDealerSelect = t6;
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      schema: searchDealerSchema,
      initialValues: {
        province: "",
        district: "",
        dealer: ""
      }
    };
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const {
    values,
    setValue,
    registerRef,
    formState,
    fieldRefs
  } = useForm(t7);
  let t8;
  if ($[5] !== fieldRefs?.current?.dealer || $[6] !== isDesktop || $[7] !== selectedDealer) {
    t8 = () => {
      if (isDesktop || !selectedDealer || !fieldRefs?.current?.dealer) {
        return;
      }
      fieldRefs?.current?.dealer?.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    };
    $[5] = fieldRefs?.current?.dealer;
    $[6] = isDesktop;
    $[7] = selectedDealer;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== fieldRefs || $[10] !== isDesktop || $[11] !== selectedDealer) {
    t9 = [fieldRefs, selectedDealer, isDesktop];
    $[9] = fieldRefs;
    $[10] = isDesktop;
    $[11] = selectedDealer;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[13] !== selectedDealer) {
    t10 = () => {
      if (!selectedDealer) {
        return;
      }
      trackDynamicEvents({
        event_name: SPECIAL_OFFER_EVENT_NAME,
        group_name: SPECIAL_OFFER_DATA_TEST.DEALER_SEARCH,
        value: selectedDealer.name_th
      });
    };
    t11 = [selectedDealer];
    $[13] = selectedDealer;
    $[14] = t10;
    $[15] = t11;
  } else {
    t10 = $[14];
    t11 = $[15];
  }
  useEffect(t10, t11);
  let t12;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      if (!contentRef.current) {
        return;
      }
      scrollIntoViewIfNeeded(contentRef.current, null, {
        block: "start"
      });
    };
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const scrollToForm = t12;
  let t13;
  if ($[17] !== className) {
    t13 = cn("bg-silver-50 scroll-mt-18 space-y-4 rounded-sm p-4 lg:rounded-md", className);
    $[17] = className;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = createDataAttributes({
      test: "dealer-search-contact"
    });
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = createDataAttributes({
      test: "title"
    });
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== title) {
    t16 = <h3 className="text-sub-h2 font-noto-bold" {...t15}>{title}</h3>;
    $[21] = title;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== formState.errors || $[24] !== provinces || $[25] !== registerRef || $[26] !== setValue || $[27] !== values) {
    t17 = <div className="grid grid-cols-2 gap-4 *:scroll-mt-18"><LocationSelector required={true} formLayout="two-column" dealerColSpan={2} dealerLabel={"\u0E28\u0E39\u0E19\u0E22\u0E4C\u0E1A\u0E23\u0E34\u0E01\u0E32\u0E23"} dealerPlaceholder={"\u0E40\u0E25\u0E37\u0E2D\u0E01\u0E28\u0E39\u0E19\u0E22\u0E4C\u0E1A\u0E23\u0E34\u0E01\u0E32\u0E23"} provinces={provinces} values={values} setValue={setValue} registerRef={registerRef} errors={formState.errors} onDealerSelect={handleDealerSelect} /></div>;
    $[23] = formState.errors;
    $[24] = provinces;
    $[25] = registerRef;
    $[26] = setValue;
    $[27] = values;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== selectedDealer) {
    t18 = !!selectedDealer && <DealerInfoCard dealer={selectedDealer} />;
    $[29] = selectedDealer;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== t13 || $[32] !== t16 || $[33] !== t17 || $[34] !== t18) {
    t19 = <div ref={contentRef} className={t13} {...t14}>{t16}{t17}{t18}</div>;
    $[31] = t13;
    $[32] = t16;
    $[33] = t17;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== hideButton || $[37] !== isFormVisible || $[38] !== title) {
    t20 = !hideButton && <CtaButtonBottom title={title} isVisible={isFormVisible} onClick={scrollToForm} />;
    $[36] = hideButton;
    $[37] = isFormVisible;
    $[38] = title;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== t19 || $[41] !== t20) {
    t21 = <>{t19}{t20}</>;
    $[40] = t19;
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  return t21;
};